import React, { useEffect, useMemo, useState } from "react";
import style from "css/admin/component.module.css";
import { Modal, Form, Button, ConfigProvider, Select, message } from "antd";
import SearchInput from "component/admin/search/searchInput";
import axios from "axios";

const roleOptions = [
    { value: "CD0101", label: "소유자" },
    { value: "CD0102", label: "관리자" },
];

function AdminModal({ isOpen, close, mode = "create", initialData = null, onSuccess }) {
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);

    const closeHandler = () => {
        form.resetFields();
        close();
    };

    useEffect(() => {
        if (!isOpen) return;

        if (mode === "edit" && initialData) {
            form.setFieldsValue({
                MEMBER_ID: initialData?.MEMBER_ID ?? "",
                MEMBER_NM: initialData?.MEMBER_NM ?? "",
                EMAIL: initialData?.EMAIL ?? "",
                MEMBER_TYPE: initialData?.MEMBER_TYPE ?? "",
                MEMBER_PW: "",
            });
        } else {
            form.resetFields();
        }
    }, [form, initialData, isOpen, mode]);

    const submit = async () => {
        try {
            const values = await form.validateFields();
            setSaving(true);

            if (mode === "edit") {
                const memberId = initialData?.MEMBER_ID;
                // 비밀번호가 빈 값이면 제거 (서버에서 빈 값이면 변경 안 함)
                const payload = { ...values };
                if (!payload.MEMBER_PW) delete payload.MEMBER_PW;
                await axios.put(`/admin/${memberId}`, payload);
                message.success("관리자 정보가 수정되었습니다.");
            } else {
                await axios.post("/admin", values);
                message.success("관리자가 등록되었습니다.");
            }

            if (typeof onSuccess === "function") onSuccess();
            closeHandler();
        } catch (err) {
            if (err?.errorFields) {
                message.error("필수 항목을 확인해주세요.");
                return;
            }
            const serverMsg = err?.response?.data ?? err?.message;
            message.error(serverMsg ? `저장 오류: ${serverMsg}` : "저장 중 오류가 발생했습니다.");
            console.log(err);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            title={
                <span className={style.searchItem_label} style={{ fontSize: 18 }}>
                    {mode === "edit" ? "관리자 수정" : "관리자 생성"}
                </span>
            }
            open={isOpen}
            onCancel={closeHandler}
            closable={false}
            maskClosable={!saving}
            styles={{ content: { borderRadius: 12 }, mask: { backdropFilter: "none" } }}
            footer={
                <ConfigProvider
                    theme={{
                        token: { colorPrimary: "#527ff2" },
                        components: {
                            Button: {
                                colorPrimaryHover: "#4a72de",
                                colorPrimaryActive: "#3f66c8",
                            },
                        },
                    }}
                >
                    <div className={style.modal_footer_wrap}>
                        <Button
                            key="cancel"
                            onClick={closeHandler}
                            disabled={saving}
                            className={style.modal_footer_btn}
                        >
                            취소
                        </Button>
                        <Button
                            key="submit"
                            type="primary"
                            onClick={submit}
                            loading={saving}
                            className={style.modal_footer_btn}
                        >
                            {mode === "edit" ? "수정" : "등록"}
                        </Button>
                    </div>
                </ConfigProvider>
            }
            centered
        >
            <Form form={form} layout="vertical" requiredMark={false} style={{ marginTop: 10 }}>
                <Form.Item
                    name="MEMBER_ID"
                    label={<span className={style.searchItem_label}>아이디</span>}
                    rules={[{ required: true, message: "아이디를 입력하세요" }]}
                >
                    <SearchInput
                        placeholder="관리자 아이디"
                        fullWidth
                        disabled={mode === "edit"}
                    />
                </Form.Item>

                <Form.Item
                    name="MEMBER_NM"
                    label={<span className={style.searchItem_label}>이름</span>}
                    rules={[{ required: true, message: "이름을 입력하세요" }]}
                >
                    <SearchInput placeholder="관리자 이름" fullWidth />
                </Form.Item>

                <Form.Item
                    name="MEMBER_PW"
                    label={
                        <span className={style.searchItem_label}>
                            비밀번호 {mode === "edit" ? "(변경 시에만 입력)" : ""}
                        </span>
                    }
                    rules={mode === "create" ? [{ required: true, message: "비밀번호를 입력하세요" }] : []}
                >
                    <SearchInput type="password" placeholder="비밀번호" fullWidth />
                </Form.Item>

                <Form.Item
                    name="EMAIL"
                    label={<span className={style.searchItem_label}>이메일</span>}
                    rules={[{ required: true, message: "이메일을 입력하세요" }]}
                >
                    <SearchInput placeholder="예) admin@example.com" fullWidth />
                </Form.Item>

                <Form.Item
                    name="MEMBER_TYPE"
                    label={<span className={style.searchItem_label}>역할</span>}
                    rules={[{ required: true, message: "역할을 선택하세요" }]}
                >
                    <Select
                        className={style.search_cascader}
                        options={roleOptions}
                        placeholder="선택해주세요"
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
}

export default AdminModal;
